import { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router';

import {
  getGetStudentQueryKey,
  getListStudentsQueryKey,
  useGetStudent,
  useUpdateStudent,
} from '../../api/generated/enrollment/enrollment';
import type { Locale, StudentAdminDetail } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { Field } from '../../design/primitives/Field';
import { AdminOnly } from './AdminOnly';
import { SelectField } from './SelectField';
import { isValidTimezone } from './timezone';
import styles from './enrollment.module.css';

const LOCALES: readonly Locale[] = ['pt-BR', 'en-US', 'es'];

/** Ficha de um aluno, com edicao do que `updateStudent` aceita. */
export function StudentDetailPage() {
  const params = useParams();
  const studentId = params['studentId'] ?? '';

  return <AdminOnly>{() => <StudentDetailView studentId={studentId} />}</AdminOnly>;
}

function StudentDetailView({ studentId }: { readonly studentId: string }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const query = useGetStudent(studentId);

  if (query.isError) return <Alert tone="error">{t('error.loadFailed')}</Alert>;
  const student = query.data;
  if (!student) return null;

  return (
    <div className={styles.page}>
      <p>
        <Link to="/students">{t('enrollment.students.backToList')}</Link>
      </p>
      {/*
        `displayName` como veio do servidor. Para o Super Admin ele ja e o nome
        completo; nao existe segundo campo de nome no contrato, e nada aqui
        recompoe partes.
      */}
      <h1>{student.displayName}</h1>

      <dl className={styles.definitions}>
        <dt>{t('enrollment.email')}</dt>
        <dd>{student.email}</dd>
        <dt>{t('enrollment.birthDate')}</dt>
        <dd>{student.birthDate ?? '—'}</dd>
        <dt>{t('enrollment.document')}</dt>
        <dd>
          {student.identificationDocuments.length === 0
            ? '—'
            : student.identificationDocuments
                .map((document) => `${t(`enrollment.documentKindLabel.${document.kind}`)}: ${document.value}`)
                .join(' · ')}
        </dd>
        <dt>{t('enrollment.guardian')}</dt>
        <dd>{student.guardian?.displayName ?? '—'}</dd>
      </dl>

      {/*
        O desempenho e leitura de outra fatia, e por isso e outra rota: a ficha
        cadastral nao carrega nota nem registro de aula. O caminho, porem,
        precisa existir daqui — era ele que faltava, e nao a tela.
      */}
      <p>
        <Link to={`/students/${student.personId}/performance`}>
          {t('enrollment.students.performanceLink')}
        </Link>
      </p>

      {/*
        A edicao muda a ficha E a listagem: desativar alguem aqui e voltar a
        /students mostrando "Ativo" era o efeito de refazer so esta consulta.
        Com `staleTime: 30_000`, a lista nem buscava de novo em segundo plano.
      */}
      <StudentEditor
        student={student}
        onSaved={() => {
          void queryClient.invalidateQueries({ queryKey: getGetStudentQueryKey(studentId) });
          void queryClient.invalidateQueries({ queryKey: getListStudentsQueryKey() });
        }}
      />
    </div>
  );
}

function StudentEditor({
  student,
  onSaved,
}: {
  readonly student: StudentAdminDetail;
  readonly onSaved: () => void;
}) {
  const { t } = useTranslation();
  const update = useUpdateStudent();

  const [phone, setPhone] = useState(student.phone ?? '');
  const [timezone, setTimezone] = useState(student.timezone);
  const [locale, setLocale] = useState<Locale>(student.locale);
  const [level, setLevel] = useState(student.level ?? '');
  const [module, setModule] = useState(student.module ?? '');
  const [objective, setObjective] = useState(student.learningObjective ?? '');
  const [active, setActive] = useState(student.active);

  // Este e o caminho pelo qual um fuso inexistente entra no cadastro: a criacao
  // e conferida no servidor, a EDICAO nao — `UpdateStudent` so recusa o campo
  // vazio. Um "Brasilia" salvo aqui derruba toda tela que formate data desta
  // pessoa, e sem `ErrorBoundary` a queda e a pagina inteira.
  const timezoneKnown = isValidTimezone(timezone);

  const submit = (): void => {
    if (!timezoneKnown) return;
    update.mutate(
      {
        studentId: student.personId,
        data: {
          phone: phone.trim() === '' ? null : phone.trim(),
          timezone: timezone.trim(),
          locale,
          level: level.trim() === '' ? null : level.trim(),
          module: module.trim() === '' ? null : module.trim(),
          learningObjective: objective.trim() === '' ? null : objective.trim(),
          active,
        },
      },
      { onSuccess: onSaved },
    );
  };

  return (
    <form
      className={styles.form}
      aria-label={t('enrollment.students.edit')}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <h2>{t('enrollment.students.edit')}</h2>
      {/*
        O nome civil nao esta aqui porque `updateStudent` nao o aceita: o corte
        da mascara e decisao do cadastro, e mudar mascara e outra conversa que o
        contrato ainda nao teve.
      */}
      <p className={styles.maskHelp}>{t('enrollment.students.nameNotEditable')}</p>

      {/* Mesmo teclado do cadastro: a edicao tambem acontece no celular. */}
      <Field
        label={t('enrollment.phone')}
        type="tel"
        inputMode="tel"
        autoComplete="tel"
        value={phone}
        onChange={(event) => setPhone(event.target.value)}
      />
      <Field
        label={t('enrollment.timezone')}
        hint={t('enrollment.timezoneHint')}
        value={timezone}
        error={timezone.trim() !== '' && !timezoneKnown ? t('enrollment.timezoneInvalid') : undefined}
        onChange={(event) => setTimezone(event.target.value)}
      />
      <SelectField
        label={t('enrollment.locale')}
        value={locale}
        options={LOCALES.map((option) => ({ value: option, label: t(`language.${option}`) }))}
        onChange={(value) => setLocale(value as Locale)}
      />
      <Field
        label={t('enrollment.level')}
        value={level}
        onChange={(event) => setLevel(event.target.value)}
      />
      <Field
        label={t('enrollment.module')}
        value={module}
        onChange={(event) => setModule(event.target.value)}
      />
      <Field
        label={t('enrollment.objective')}
        value={objective}
        onChange={(event) => setObjective(event.target.value)}
      />
      <label className={styles.toggle}>
        <input
          type="checkbox"
          checked={active}
          onChange={(event) => setActive(event.target.checked)}
        />
        {t('enrollment.activeRegistration')}
      </label>

      {update.isError ? <Alert tone="error">{t('error.unexpected')}</Alert> : null}
      {update.isSuccess ? <Alert tone="success">{t('enrollment.students.saved')}</Alert> : null}

      <div>
        <Button
          variant="primary"
          type="submit"
          loading={update.isPending}
          disabled={!timezoneKnown}
        >
          {t('enrollment.save')}
        </Button>
      </div>
    </form>
  );
}
